import { cn } from '@/utils/tailwind-util';
import React, { useState } from 'react';

export interface CustomButtonProps {
  variant?:
    | 'solid'
    | 'solid-light'
    | 'ghost'
    | 'text'
    | 'text-primary'
    | 'text-danger';
  size?: 'xs' | 'sm' | 'md' | 'lg';
  shadowed?: 'true' | 'false';
  rounded?: boolean;
  hasPadding?: boolean;
  iconElement?: React.ReactNode[];
  styles?: string;
}

export const useButtonVariant = (
  defaultVariant: CustomButtonProps['variant'] = 'solid'
) => useState<CustomButtonProps['variant']>(defaultVariant);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type Props = React.ButtonHTMLAttributes<any> & CustomButtonProps;

const StyledButtonIcon = ({
  size,
  children,
}: {
  size: Props['size'];
  children: React.ReactNode;
}) => {
  return (
    <span
      className={cn(
        'inline-block',
        size === 'sm'
          ? 'w-4 h-4'
          : size === 'md'
          ? 'w-5 h-5'
          : size === 'lg'
          ? 'w-6 h-6'
          : ''
      )}
    >
      {children}
    </span>
  );
};
const Button = React.forwardRef<HTMLButtonElement, Props>(
  (
    {
      type = 'button',
      size = 'md',
      iconElement,
      children,
      onClick,
      onFocus,
      className,
      hasPadding = true,
      rounded = false,
      ...rest
    },
    buttonRef
  ) => {
    return (
      <button
        className={cn(
          'flex items-center justify-center font-medium border rounded disabled:cursor-auto gap-1.5',
          // getButtonVaraintStyle(rest?.variant),
          rest.variant === 'solid' &&
            'bg-primary border-primary text-white focus:bg-primary-600 focus:border-primary-600 disabled:bg-gray-100 disabled:text-gray-400 disabled:border-gray-100',
          rest.variant === 'solid-light' &&
            'bg-primary-100 text-primary-600 border-primary-100 focus:bg-primary-200 focus:border-primary-200 disabled:text-primary-300',
          rest.variant === 'ghost' &&
            'bg-white text-primary-600 border-primary focus:bg-primary-50 focus:border-primary-600 disabled:text-gray-300 disabled:border-gray-300',
          rest.variant === 'text' &&
            'bg-transparent text-primary border-transparent focus:bg-gray-100 focus:border-gray-100 disabled:opacity-50',
          rest.variant === 'text-primary' &&
            'bg-transparent text-primary border-transparent focus:bg-primary-100 focus:border-primary-100 disabled:opacity-50',
          rest.variant === 'text-danger' &&
            'bg-gray-100 text-system-red border-gray-100 focus:bg-system-red focus:bg-opacity-10 focus:border-red-100 disabled:opacity-50',
          rest.shadowed === 'true' && 'shadow-md focus:shadow-inner',
          rounded && 'px-5 rounded-full',
          size === 'xs' && 'px-3 py-2 text-xs max-h-[32px]',
          size === 'sm' && 'px-3 py-2 text-sm max-h-[36px]',
          size === 'md' && 'px-6 py-3 text-base max-h-[48px]',
          size === 'lg' && 'px-6 py-3 text-lg max-h-[52px]',
          !hasPadding && 'p-0',
          rest.styles && rest.styles,
          className && className
        )}
        type={type}
        ref={buttonRef}
        // @ts-ignore
        size={size}
        onClick={onClick}
        onFocus={onFocus}
        {...rest}
      >
        {iconElement?.[0] && (
          <StyledButtonIcon size={size} aria-hidden="true">
            {iconElement[0]}
          </StyledButtonIcon>
        )}
        <span className="inline-flex items-center">{children}</span>
        {iconElement?.[1] && (
          <StyledButtonIcon size={size} aria-hidden="true">
            {iconElement[1]}
          </StyledButtonIcon>
        )}
      </button>
    );
  }
);

Button.displayName = 'Button';

Button.defaultProps = {
  type: 'button',
  disabled: false,
  variant: 'solid',
  size: 'md',
  shadowed: 'false',
  iconElement: [null, null],
};

export default Button;
